import React, { useEffect, useRef, useState, useCallback } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import location from "../../Assets/Icons/Location.svg";
import SearchBar from "./SearchBar";

const GoogleMaps = ({
  onLocationSelect,
  searchContainerClass,
  searchBarParentClassName,
  compactMode = false, // when true show only a search icon button instead of full inline search on desktop
  externalSearchValue, // optional: value typed in parent input
  forceOpenSearch = false, // when true, open overlay search and show suggestions using external value
}) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
    libraries: ["geometry", "drawing", "places"],
  });

  const [position, setPosition] = useState({ lat: -37.8136, lng: 144.9631 });
  const [zoomLevel, setZoomLevel] = useState(10); // Initial zoom level
  const [searchInput, setSearchInput] = useState("");
  const [placeSelected, setPlaceSelected] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const mapRef = useRef(null);
  const placesService = useRef(null);
  const debounceTimerRef = useRef(null);


  const handleMarkerDragEnd = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      setPosition({ lat, lng });

      // Get place information using the Google Places API
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const address = results[0].formatted_address;
          const comps = results[0].address_components || [];
          const get = (type) => (comps.find(c => c.types.includes(type)) || {});
          const city = get('locality').long_name || get('administrative_area_level_2').long_name || "";
          const state = get('administrative_area_level_1').short_name || get('administrative_area_level_1').long_name || "";
          const country = get('country').long_name || "";
          const zipCode = get('postal_code').long_name || "";
          onLocationSelect({
            lat,
            lng,
            placeId: results[0].place_id,
            placeName: address,
            city,
            state,
            country,
            zipCode,
          });
          setSearchInput(address); // Update the address input field
        }
      });

      // Access map instance and zoom in to the new position
      const map = mapRef.current;
      if (map) {
        // Set zoom level and pan the map to the new marker position
        map.setZoom(12); // Zoom in
        map.panTo({ lat, lng }); // Center the map on the new location
        setZoomLevel(20); // Update zoom level state
      }
    }, 1500);
  };

  const handleUserSearch = useCallback(() => {
    if (!searchInput.trim()) {
      setSearchError("Please enter an address to search");
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setSearchError("");
    const request = {
      input: searchInput,
      fields: [
        "place_id",
        "description",
        "structured_formatting",
        "terms",
        "types",
      ],
    };
    const autocompleteService =
      new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(request, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        // Transform results to match SearchBar expected format
        const transformedResults = results.map((result) => ({
          id: result.place_id,
          title: result.structured_formatting?.main_text || result.description,
          description: result.structured_formatting?.secondary_text || result.description,
          place_id: result.place_id,
          original: result
        }));
        setSearchResults(transformedResults);
        setShowResults(true);
        setSearchError("");
      } else {
        setSearchResults([]);
        setShowResults(false);
        setSearchError("No locations found. Please try a different address.");
      }
    });
  }, [searchInput]);

  const handlePlaceSelect = (place) => {
    const map = mapRef.current;
    if (map) {
      const service = new window.google.maps.places.PlacesService(map);
      service.getDetails(
        {
          placeId: place.place_id,
          fields: ["geometry", "formatted_address", "address_component"],
        },
        (result, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const { geometry, formatted_address } = result;
            if (geometry) {
              const { location } = geometry;
              map.panTo(location);
              setPosition({ lat: location.lat(), lng: location.lng() });
              setZoomLevel(12); // Zoom in when place is selected
              map.setZoom(12); // Explicitly zoom the map
              const comps = (result.address_components || result.address_component || []);
              const get = (type) => (comps.find(c => (c.types||[]).includes(type)) || {});
              const city = get('locality').long_name || get('administrative_area_level_2').long_name || "";
              const state = get('administrative_area_level_1').short_name || get('administrative_area_level_1').long_name || "";
              const country = get('country').long_name || "";
              const zipCode = get('postal_code').long_name || "";
              onLocationSelect({
                lat: location.lat(),
                lng: location.lng(),
                placeId: place.place_id,
                placeName: formatted_address,
                city,
                state,
                country,
                zipCode,
              });
              setSearchInput(formatted_address); // Update the address input field
              setPlaceSelected(true);
              setSearchResults([]);
              setShowResults(false);
              setSearchError("");
              setIsMobileSearchExpanded(false); // Close mobile search when place is selected
            }
          } else {
            setSearchError("Unable to get location details. Please try another address.");
          }
        }
      );
    }
  };

  const handleInputChange = (e) => {
    setSearchInput(e.target.value);
    setPlaceSelected(false);
    setSearchError("");
    if (e.target.value.trim() === "") {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleUserSearch();
    }
  };

  const handleSearchIconClick = () => {
    handleUserSearch();
  };

  const handleMobileSearchIconClick = () => {
    setIsMobileSearchExpanded(true);
    // Focus on the mobile search input after a short delay to ensure it's rendered
    setTimeout(() => {
      const mobileSearchInput = document.querySelector('.mobile-search-input');
      if (mobileSearchInput) {
        mobileSearchInput.focus();
      }
    }, 100);
  };

  const handleMobileSearchClose = () => {
    setIsMobileSearchExpanded(false);
    setShowResults(false);
  };

  const handleInputBlur = () => {
    // Delay hiding results to allow click on results
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  const handleMobileInputBlur = () => {
    // Delay hiding results to allow click on results
    setTimeout(() => {
      setShowResults(false);
      // Don't auto-close mobile search on blur, let user manually close it
    }, 200);
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (placeSelected === false && searchInput.trim().length > 2) {
        handleUserSearch();
      }
    }, 1000);
    return () => clearTimeout(debounceTimer);
  }, [searchInput, placeSelected, handleUserSearch]);

  // When parent asks to open search with a query (e.g., user typing in the top input)
  useEffect(() => {
    if (!compactMode) return;
    const value = (externalSearchValue || "").trim();
    if (!forceOpenSearch || value.length === 0) return;
    setIsMobileSearchExpanded(true);
    setSearchInput(value);
    setPlaceSelected(false);
    // Immediately fetch suggestions for the provided value
    const autocompleteService =
      window.google && window.google.maps && window.google.maps.places
        ? new window.google.maps.places.AutocompleteService()
        : null;
    if (autocompleteService) {
      autocompleteService.getPlacePredictions(
        { input: value },
        (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            const transformedResults = results.map((result) => ({
              id: result.place_id,
              title: result.structured_formatting?.main_text || result.description,
              description: result.structured_formatting?.secondary_text || result.description,
              place_id: result.place_id,
              original: result
            }));
            setSearchResults(transformedResults);
            setShowResults(true);
            setSearchError("");
          } else {
            setSearchResults([]);
            setShowResults(false);
          }
        }
      );
    }
  }, [externalSearchValue, forceOpenSearch, compactMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isLoaded && (
        <>
          <GoogleMap
            mapContainerClassName="swoodle-map h-[260px] sm:h-[360px] w-full rounded-[8px] mt-[1rem] relative"
            zoom={zoomLevel} // Dynamically control zoom level
            center={position}
            onLoad={(map) => {
              mapRef.current = map;
              placesService.current =
                new window.google.maps.places.PlacesService(map);
            }}
          >
            {position && (
              <Marker
                icon={{
                  url: location,
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
                position={position}
                draggable={true}
                onDragEnd={handleMarkerDragEnd}
              />
            )}
            
            {/* Search Icon (always on when compactMode; mobile-only otherwise) */}
            <div className={`absolute z-[10] bottom-[160px] right-[8px] ${compactMode ? 'block' : 'sm:hidden'}`}>
              <button
                onClick={handleMobileSearchIconClick}
                className="w-[40px] h-[40px] bg-white rounded-[6px] shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </div>

            {/* Expanded Search Bar (visible on mobile; and on desktop when compactMode is true) */}
            {isMobileSearchExpanded && (
              <div className={`absolute z-[10] top-[8px] left-[8px] right-[8px] ${compactMode ? 'block' : 'sm:hidden'}`}>
                <div className="bg-white rounded-[6px] shadow-lg p-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="mobile-search-input flex-1 h-[36px] px-3 text-[12px] font-[400] font-poppins outline-none bg-gray-50 text-[#303030] rounded-[4px] border border-gray-200"
                      placeholder="Enter your address and press Enter"
                      value={searchInput}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyPress}
                      onBlur={handleMobileInputBlur}
                      autoFocus
                    />
                    <button
                      onClick={handleMobileSearchClose}
                      className="w-[36px] h-[36px] bg-gray-100 rounded-[4px] flex items-center justify-center hover:bg-gray-200 transition-colors"
                    >
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Mobile Search Results */}
                  {showResults && searchResults.length > 0 && (
                    <div className="mt-2 max-h-[200px] overflow-y-auto">
                      {searchResults.map((result, index) => (
                        <div
                          key={index}
                          className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handlePlaceSelect(result)}
                        >
                          <p className="text-[12px] font-medium text-gray-800">
                            {result.title}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {result.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Mobile Search Error */}
                  {searchError && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-[4px]">
                      <p className="text-red-600 text-[10px] font-poppins">{searchError}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Desktop Search Bar (hidden entirely when compactMode is true) */}
            {!compactMode && (
              <div className="hidden sm:block">
                <SearchBar
                  parentDivClass={
                    searchBarParentClassName
                      ? searchBarParentClassName
                      : `absolute z-[10] top-2 right-[12px] w-[70%] md:w-[60%] lg:w-[55%] xl:w-[50%] 2xl:w-[45%] max-w-[680px] ${searchContainerClass || ''}`
                  }
                  divClassName="w-full flex items-center h-[32px] md:h-[36px] relative"
                  inputClassName="navSearchBar h-full w-[100%] ps-[3%] pe-[12%] text-[10px] md:text-[12px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[6px]"
                  iconClassName="absolute right-[4%]"
                  placeholder="Enter your address and press Enter"
                  value={searchInput}
                  handleChange={handleInputChange}
                  handleKeyDown={handleKeyPress}
                  onBlur={handleInputBlur}
                  searchResults={searchResults}
                  handleResultClick={handlePlaceSelect}
                  resultsVisible={showResults}
                  noResultsText={searchError || "No locations found"}
                  onIconClick={handleSearchIconClick}
                />
              </div>
            )}
          </GoogleMap>
          {searchError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm font-poppins">{searchError}</p>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default GoogleMaps;
